/**
 * @module builder
 */
import type { QuoteOptions, QuoteArea, OptionsDocument, QuoteDocument } from 'paintscout'
import get from 'lodash/get'
import type { RenderableSubItem, UpdateableAreaSubstrate } from '../../../../index'
import { getItemDetails } from '../../util/get-item-details'
import { getCrewDescription } from '../../util/get-crew-description'
import { combineReportText } from '../../util/combine-report-text'
import { resolveDisplayCoats } from '../../../../util/resolve-product-coats'
import getDimensions from './get-dimensions'

/**
 * Convert a UpdateableAreaSubstrate and QuoteArea to a RenderableSubItem
 * to be viewed by crew within substrate on WorkOrders
 */
export default function toRenderableCrewSubstrateArea({
  key,
  quote,
  area,
  item,
  showValues,
  quoteOptions,
  options,
  hideDimensions
}: {
  key: string
  quote: QuoteDocument
  area: QuoteArea
  item: UpdateableAreaSubstrate
  showValues: boolean
  quoteOptions: QuoteOptions
  options: OptionsDocument
  hideDimensions?: boolean
}): RenderableSubItem {
  const { prep, hours, price, value, rateItemLabel, rateClientLabel } = getItemDetails({
    item,
    quote
  })

  const { crewDescription, crewDescriptionParts } = getCrewDescription({
    prep,
    hours,
    itemName: rateItemLabel,
    clientLabel: rateClientLabel,
    showValues,
    products: item.products,
    quoteOptions,
    options,
    substrate: true
  })

  let name = area?.area_label?.value ?? rateItemLabel
  const rateType = item?.rate?.rate_type ?? item?.rate?.rateType ?? ''
  const areaType = get(area, 'area_type.value', 'room')
  // Substrate text first, then Area, to match the order in the "Show {Substrate} and Area
  // View Descriptions on Work Order" setting label and the area-grouped WO converter.
  const reportText = combineReportText(item?.rate?.reportText, item?.rate?.areaReportText)

  const { label: dimensionLabel, value: dimensionValue } = hideDimensions
    ? { label: '', value: 0 }
    : getDimensions({ rateType, areaType, item, area, options })

  const dimensionString = [dimensionValue, dimensionLabel].join('')
  if (dimensionString) {
    name = `${name}${dimensionString === '0' ? '' : ` (${dimensionString})`}`
  }

  return {
    key,
    name,
    description: crewDescription,
    dimension: {
      label: dimensionLabel,
      value: dimensionValue
    },
    reportText,
    crewNote: item.notes,
    value,
    additionalFields: {
      prep,
      // A product may carry its own coats override; when products disagree, show every distinct
      // value rather than the substrate's single (potentially wrong) coats.
      coats: item?.rate?.useCoats ? resolveDisplayCoats(item.coats ?? 0, item.products ?? []) : 0,
      painting: hours,
      price,
      // The description split into its parts, so a view can render one line each / drop the products.
      crewDescriptionParts
    }
  }
}
